import React from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { useWorkspaceContext } from "@/contexts/workspace";

/**
 * Resolves a stable workspaceId in the URL to the per-user workspace index expected by the rest of the router.
 *
 * Email links from the backend can't know the recipient's workspace ordering (it's a per-user runtime list — see
 * {@code useDefaultWorkspace}), so they target {@code #/ws/{workspaceId}/<rest>} and this component swaps in the
 * correct index for the user that opened the link.
 */
export const WorkspaceIdRedirect: React.FC = () => {
  const params = useParams<{ workspaceId: string; "*": string }>();
  const ctx = useWorkspaceContext() as { workspace?: { workspaces?: { id: string | number }[] } };
  const workspaces = ctx.workspace?.workspaces ?? [];
  const location = useLocation();

  // Workspaces load async; render nothing until the list is available.
  if (workspaces.length === 0) return null;

  const target = String(params.workspaceId ?? "");
  const index = workspaces.findIndex((w) => String(w.id) === target);
  if (index < 0) {
    return <Navigate to="/" replace />;
  }
  const rest = params["*"] ?? "";
  const search = location.search ?? "";
  return <Navigate to={`/${index}/${rest}${search}`} replace />;
};
